import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader, Sparkles, Tv, Film, Package, Crown, Lock, Star, Smartphone } from 'lucide-react';
import { Toaster } from 'react-hot-toast'
import { supabase } from './lib/supabase'
import Logo from './components/Logo';
import { useAuth } from './contexts/AuthContext';
import SignUp from './components/SignUp';
import Login from './components/Login';
import SmartProfileEditor from './components/SmartProfileEditor';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import AuthConfirm from './components/AuthConfirm';
import ResetPassword from './components/ResetPassword';
import Landing from './components/Landing';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import OnboardingPrompt from './components/onboarding/OnboardingPrompt';
import WelcomeBanner from './components/WelcomeBanner';
import { AdminApp } from './components/admin';
import { getISPsForZip } from './utils/ispCoverage';
import UpgradeBanner, { PremiumBadge } from './components/UpgradeBanner';
import FeedbackButtons from './components/FeedbackButtons';

// Wrapper that checks for admin route before rendering main app
function AppRouter() {
  if (window.location.pathname.startsWith('/admin')) {
    return <AdminApp />;
  }
  return <StreamingAdvisor />;
}

export default AppRouter;

function StreamingAdvisor() {
const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
 
  // Auth state
  const { user, loading: authLoading, signOut } = useAuth();
  const [showSignUp, setShowSignUp] = useState(true); // true = signup, false = login
  const [showProfile, setShowProfile] = useState(false);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(null); // null = unknown, loading;
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [zipCode, setZipCode] = useState('');
  const [internetProvider, setInternetProvider] = useState('');
  const [internetPlan, setInternetPlan] = useState('');
  const [currentServices, setCurrentServices] = useState([]);

  // Profile completion (second stage onboarding)
const [showProfileWizard, setShowProfileWizard] = useState(false);
const [profileCompleted, setProfileCompleted] = useState(true); // assume true until we check
const [showProfilePrompt, setShowProfilePrompt] = useState(false);

// Eligibility flags
const [isStudent, setIsStudent] = useState(false);
const [isSenior, setIsSenior] = useState(false);
const [isVeteran, setIsVeteran] = useState(false);
const [isFirstResponder, setIsFirstResponder] = useState(false);

// Cellular provider state
const [cellularProvider, setCellularProvider] = useState('');
const [cellularPlan, setCellularPlan] = useState('');
const [cellularLines, setCellularLines] = useState(1);
const [cellularProviders, setCellularProviders] = useState([]);
const [cellularPlans, setCellularPlans] = useState([]);
const [cellularPerks, setCellularPerks] = useState([]);
const [loadingCellular, setLoadingCellular] = useState(false);

// Bundle selection state
const [availableBundles, setAvailableBundles] = useState([]);
const [selectedBundles, setSelectedBundles] = useState([]);
const [loadingBundles, setLoadingBundles] = useState(false);

// Subscription status
const [userPlanType, setUserPlanType] = useState('free');

  const messagesEndRef = useRef(null);

// Reference data from database
const [refStreamingServices, setRefStreamingServices] = useState([]);
const [refInternetProviders, setRefInternetProviders] = useState([]);

// ISP coverage data (filtered by zip code)
const [ispCoverage, setIspCoverage] = useState([]);
const [loadingISPs, setLoadingISPs] = useState(false);
const [ispPerks, setIspPerks] = useState([]);
const [ispPlans, setIspPlans] = useState([]);
const [selectedPlan, setSelectedPlan] = useState('');
const [loadingPlans, setLoadingPlans] = useState(false);
  
// Streaming services for checklist (use database or fallback)
const streamingServices = refStreamingServices.length > 0 
  ? refStreamingServices 
  : [
      'Netflix', 'Disney+', 'Hulu', 'Amazon Prime Video', 'Apple TV+',
      'Max (HBO Max)', 'Paramount+', 'Peacock', 'Discovery+', 'ESPN+',
      'YouTube TV', 'Sling TV', 'FuboTV', 'Showtime', 'Starz',
      'AMC+', 'BET+', 'Crunchyroll', 'Shudder', 'Other'
    ];

// Get ISP options - prefer coverage data filtered by zip, fall back to isp_perks
const getInternetProviders = () => {
  // If we have coverage data for this zip, use it
  if (ispCoverage.length > 0) {
    return ispCoverage;
  }
  
  // Fall back to refInternetProviders from isp_perks
  if (refInternetProviders.length > 0) {
    return refInternetProviders.map(name => ({ isp_name: name }));
  }
  
  // Ultimate fallback
  return [
    { isp_name: 'Xfinity (Comcast)' },
    { isp_name: 'Spectrum' },
    { isp_name: 'AT&T' },
    { isp_name: 'Verizon Fios' },
    { isp_name: 'Cox' },
    { isp_name: 'Optimum' },
    { isp_name: 'CenturyLink' },
    { isp_name: 'Frontier' },
    { isp_name: 'T-Mobile Home Internet' },
    { isp_name: 'Starlink' },
  ];
};

const internetProviders = getInternetProviders();

// Load ISP coverage when zip code changes
useEffect(() => {
  if (zipCode.length === 5) {
    loadISPCoverage(zipCode);
  } else {
    setIspCoverage([]);
    setInternetProvider('');
  }
}, [zipCode]);

// Load available bundles when provider selections change
useEffect(() => {
  if (onboardingStep === 4) {
    loadAvailableBundles();
  }
}, [onboardingStep, cellularProvider, cellularPlan, internetProvider]);

const loadISPCoverage = async (zip) => {
  setLoadingISPs(true);
  try {
    // Use tiered coverage lookup (national + state-level + metro)
    const tieredISPs = getISPsForZip(zip);
    
    console.log('Tiered ISP lookup for zip', zip, ':', tieredISPs);
    
    // Convert to flat list format for compatibility
    const flatList = [
      // Fiber providers first (most desirable)
      ...tieredISPs.regional.filter(i => i.type === 'fiber').map(i => ({
        isp_name: i.name,
        isp_code: i.code,
        coverage_type: 'fiber',
        coverage_quality: i.quality,
        check_url: i.checkUrl
      })),
      // Metro-specific fiber (Google Fiber, etc.)
      ...tieredISPs.metro.filter(i => i.type === 'fiber').map(i => ({
        isp_name: i.name + ' (' + i.metro + ')',
        isp_code: i.code,
        coverage_type: 'fiber',
        coverage_quality: i.quality,
        metro: i.metro,
        check_url: i.checkUrl
      })),
      // Cable providers
      ...tieredISPs.regional.filter(i => i.type === 'cable').map(i => ({
        isp_name: i.name,
        isp_code: i.code,
        coverage_type: 'cable',
        coverage_quality: i.quality,
        check_url: i.checkUrl
      })),
      // 5G Fixed Wireless
      ...tieredISPs.national.filter(i => i.type === 'fixed_wireless').map(i => ({
        isp_name: i.name,
        isp_code: i.code,
        coverage_type: '5G wireless',
        coverage_quality: i.quality,
        note: i.note,
        check_url: i.checkUrl
      })),
      // Satellite (always available as fallback)
      ...tieredISPs.national.filter(i => i.type === 'satellite').map(i => ({
        isp_name: i.name,
        isp_code: i.code,
        coverage_type: 'satellite',
        coverage_quality: i.quality,
        note: i.note,
        check_url: i.checkUrl
      }))
    ];

    console.log('Flat ISP list:', flatList);

    if (flatList.length > 0) {
      setIspCoverage(flatList);
    } else {
      console.log('No coverage data found, will fall back to isp_perks');
      setIspCoverage([]);
    }
  } catch (error) {
    console.error('Error loading ISP coverage:', error);
    setIspCoverage([]);
  } finally {
    setLoadingISPs(false);
  }
};

// Get perks for a specific ISP
const getISPPerks = (ispName) => {
  return ispPerks.filter(p => 
    p.isp_name?.toLowerCase() === ispName?.toLowerCase() &&
    p.service_name &&
    p.service_name.toLowerCase() !== 'none'
  );
};

// Get plans for a specific ISP
const getPlansForISP = (ispName) => {
  // Find the isp_code from coverage or perks data
  const coverageMatch = ispCoverage.find(c => c.isp_name?.toLowerCase() === ispName?.toLowerCase());
  const ispCode = coverageMatch?.isp_code;
  
  if (!ispCode) return [];
  
  return ispPlans.filter(p => p.isp_code === ispCode).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

// Get plans for selected cellular provider
const getPlansForCellular = (providerCode) => {
  if (!providerCode) return [];
  const provider = cellularProviders.find(p => p.provider_code === providerCode);
  if (!provider) return [];
  return cellularPlans.filter(p => p.provider_id === provider.id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

// Get perks for selected cellular plan
const getPerksForCellularPlan = (providerCode, planCode) => {
  if (!providerCode) return [];
  return cellularPerks.filter(p => {
    const provider = cellularProviders.find(cp => cp.id === p.provider_id);
    if (provider?.provider_code !== providerCode) return false;
    if (!p.eligible_plan_codes) return true; // Applies to all plans
    return p.eligible_plan_codes.includes(planCode);
  });
};

// Check if user has same provider for cell and home internet (convergence eligibility)
const checkConvergenceEligibility = () => {
  if (!cellularProvider || !internetProvider) return false;
  
  // Map ISP names to cellular provider codes
  const ispToCellular = {
    'verizon fios': 'verizon',
    'verizon 5g home': 'verizon',
    't-mobile home internet': 'tmobile',
    'at&t': 'att',
    'at&t fiber': 'att',
  };
  
  const normalizedISP = internetProvider.toLowerCase();
  const matchingCellular = ispToCellular[normalizedISP];
  
  return matchingCellular === cellularProvider;
};

// Load available bundles based on user's selections
const loadAvailableBundles = async () => {
  setLoadingBundles(true);
  try {
    // Get ALL bundles from database (streaming bundles only, not convergence offers)
    const { data: bundles } = await supabase
      .from('bundles')
      .select('*, bundle_services(service_name, included_tier)')
      .eq('is_active', true)
      .order('monthly_cost');
    
    let available = [];
    
    // Add all bundles for user to select from
    if (bundles && bundles.length > 0) {
      available = bundles.map(b => ({
        ...b,
        id: b.id,
        bundle_name: b.bundle_name,
        bundle_code: b.bundle_code,
        source_type: 'streaming_bundle',
        monthly_cost: b.monthly_cost || 0,
        description: b.notes || `Includes: ${b.bundle_services?.map(s => s.service_name).join(', ') || 'Multiple services'}`
      }));
    }
    
    // If no bundles in database or fewer than expected, provide comprehensive bundle options
    if (available.length < 10) {
      available = [
        // Disney Duo Bundles
        {
          id: 'disney_duo_basic',
          bundle_name: 'Disney Bundle Duo Basic (Disney+ & Hulu with Ads)',
          bundle_code: 'disney_duo_basic',
          source_type: 'streaming_bundle',
          monthly_cost: 10.99,
          description: 'Disney+ (with ads) and Hulu (with ads)'
        },
        {
          id: 'disney_duo_premium',
          bundle_name: 'Disney Bundle Duo Premium (Disney+ & Hulu No Ads)',
          bundle_code: 'disney_duo_premium',
          source_type: 'streaming_bundle',
          monthly_cost: 19.99,
          description: 'Disney+ (no ads) and Hulu (no ads)'
        },
        // Disney Trio Bundles (with ESPN+)
        {
          id: 'disney_trio_basic',
          bundle_name: 'Disney Bundle Trio Basic (Disney+, Hulu, ESPN+ with Ads)',
          bundle_code: 'disney_trio_basic',
          source_type: 'streaming_bundle',
          monthly_cost: 16.99,
          description: 'Disney+ (with ads), Hulu (with ads), and ESPN+'
        },
        {
          id: 'disney_trio_premium',
          bundle_name: 'Disney Bundle Trio Premium (Disney+, Hulu No Ads, ESPN+)',
          bundle_code: 'disney_trio_premium',
          source_type: 'streaming_bundle',
          monthly_cost: 26.99,
          description: 'Disney+ (no ads), Hulu (no ads), and ESPN+'
        },
        // Disney + Hulu + Max Bundles
        {
          id: 'disney_hulu_max_basic',
          bundle_name: 'Disney+, Hulu & Max Bundle (with Ads)',
          bundle_code: 'disney_hulu_max_basic',
          source_type: 'streaming_bundle',
          monthly_cost: 16.99,
          description: 'Disney+ (with ads), Hulu (with ads), and Max (with ads)'
        },
        {
          id: 'disney_hulu_max_premium',
          bundle_name: 'Disney+, Hulu & Max Bundle (No Ads)',
          bundle_code: 'disney_hulu_max_premium',
          source_type: 'streaming_bundle',
          monthly_cost: 29.99,
          description: 'Disney+ (no ads), Hulu (no ads), and Max (no ads)'
        },
        // Paramount+ Bundles
        {
          id: 'paramount_showtime',
          bundle_name: 'Paramount+ with Showtime',
          bundle_code: 'paramount_showtime',
          source_type: 'streaming_bundle',
          monthly_cost: 12.99,
          description: 'Paramount+ Essential and Showtime bundled together'
        },
        {
          id: 'paramount_showtime_premium',
          bundle_name: 'Paramount+ with Showtime (No Ads)',
          bundle_code: 'paramount_showtime_premium',
          source_type: 'streaming_bundle',
          monthly_cost: 15.99,
          description: 'Paramount+ (no ads) and Showtime bundled together'
        },
        // Apple Bundles
        {
          id: 'apple_one_individual',
          bundle_name: 'Apple One Individual',
          bundle_code: 'apple_one_individual',
          source_type: 'streaming_bundle',
          monthly_cost: 19.95,
          description: 'Apple TV+, Apple Music, Apple Arcade, 50GB iCloud+'
        },
        {
          id: 'apple_one_family',
          bundle_name: 'Apple One Family',
          bundle_code: 'apple_one_family',
          source_type: 'streaming_bundle',
          monthly_cost: 25.95,
          description: 'Apple TV+, Apple Music, Apple Arcade, 200GB iCloud+ (up to 5 family members)'
        },
        {
          id: 'apple_one_premier',
          bundle_name: 'Apple One Premier',
          bundle_code: 'apple_one_premier',
          source_type: 'streaming_bundle',
          monthly_cost: 37.95,
          description: 'Apple TV+, Apple Music, Apple Arcade, Apple News+, Apple Fitness+, 2TB iCloud+'
        },
        // YouTube Bundles
        {
          id: 'youtube_premium_family',
          bundle_name: 'YouTube Premium Family',
          bundle_code: 'youtube_premium_family',
          source_type: 'streaming_bundle',
          monthly_cost: 22.99,
          description: 'YouTube Premium and YouTube Music for up to 5 family members'
        },
        // Amazon Bundles
        {
          id: 'amazon_prime',
          bundle_name: 'Amazon Prime (includes Prime Video)',
          bundle_code: 'amazon_prime',
          source_type: 'streaming_bundle',
          monthly_cost: 14.99,
          description: 'Prime Video plus free shipping, Prime Music, and more'
        },
        // Walmart+ Bundle
        {
          id: 'walmart_plus',
          bundle_name: 'Walmart+ (includes Paramount+ Essential)',
          bundle_code: 'walmart_plus',
          source_type: 'streaming_bundle',
          monthly_cost: 12.95,
          description: 'Walmart+ membership with free Paramount+ Essential included'
        }
      ];
    }
    
    setAvailableBundles(available);
  } catch (error) {
    console.error('Error loading bundles:', error);
    // Provide fallback bundles on error
    setAvailableBundles([
      {
        id: 'disney_duo_basic',
        bundle_name: 'Disney Bundle Duo Basic (Disney+ & Hulu with Ads)',
        bundle_code: 'disney_duo_basic',
        source_type: 'streaming_bundle',
        monthly_cost: 10.99,
        description: 'Disney+ (with ads) and Hulu (with ads)'
      },
      {
        id: 'disney_duo_premium',
        bundle_name: 'Disney Bundle Duo Premium (Disney+ & Hulu No Ads)',
        bundle_code: 'disney_duo_premium',
        source_type: 'streaming_bundle',
        monthly_cost: 19.99,
        description: 'Disney+ (no ads) and Hulu (no ads)'
      },
      {
        id: 'disney_trio_basic',
        bundle_name: 'Disney Bundle Trio Basic (Disney+, Hulu, ESPN+ with Ads)',
        bundle_code: 'disney_trio_basic',
        source_type: 'streaming_bundle',
        monthly_cost: 16.99,
        description: 'Disney+ (with ads), Hulu (with ads), and ESPN+'
      }
    ]);
  } finally {
    setLoadingBundles(false);
  }
};

// Toggle bundle selection
const toggleBundle = (bundleId) => {
  setSelectedBundles(prev => 
    prev.includes(bundleId)
      ? prev.filter(id => id !== bundleId)
      : [...prev, bundleId]
  );
};

// Get services that are already covered by selected bundles
const getServicesInBundles = () => {
  const services = new Set();
  selectedBundles.forEach(bundleId => {
    const bundle = availableBundles.find(b => b.id === bundleId || b.bundle_code === bundleId);
    if (bundle?.bundle_services) {
      bundle.bundle_services.forEach(s => services.add(s.service_name));
    }
    // Handle cellular perks that include multiple services
    if (bundle?.service_name) {
      // Parse bundle names like "Netflix & Max Bundle"
      const bundleServices = bundle.service_name.split(/[&,]/).map(s => s.trim());
      bundleServices.forEach(s => services.add(s));
    }
  });
  return services;
};

  // Handle service selection
  const toggleService = (service) => {
    setCurrentServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  // Handle onboarding completion
 const completeOnboarding = async () => {
  try {
    // Save user data to users table
const { error: userError } = await supabase
  .from('users')
  .update({
    zip_code: zipCode,
    internet_provider: internetProvider,
    internet_plan: selectedPlan || null,
    cellular_provider: cellularProvider !== 'none' ? cellularProvider : null,
    cellular_plan: cellularPlan || null,
    cellular_lines: cellularLines || 1,
    is_student: isStudent,
    is_senior: isSenior,
    is_veteran: isVeteran,
    is_first_responder: isFirstResponder,
    updated_at: new Date().toISOString()
  })
  .eq('id', user.id);

    if (userError) throw userError;

    // Save cellular provider data if selected
    if (cellularProvider && cellularProvider !== 'none') {
      const provider = cellularProviders.find(p => p.provider_code === cellularProvider);
      const plan = cellularPlans.find(p => p.plan_code === cellularPlan);
      
      // Check if same provider as ISP for convergence
      const hasConvergence = checkConvergenceEligibility();
      
      const { error: cellError } = await supabase
        .from('user_cellular')
        .upsert({
          user_id: user.id,
          provider_id: provider?.id,
          plan_id: plan?.id,
          provider_code: cellularProvider,
          plan_code: cellularPlan,
          num_lines: cellularLines,
          same_provider_as_isp: hasConvergence,
          linked_isp_code: hasConvergence ? internetProvider : null,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (cellError) {
        console.error('Error saving cellular data:', cellError);
      }
    }

    // Save selected bundles
    if (selectedBundles.length > 0) {
      for (const bundleId of selectedBundles) {
        const bundle = availableBundles.find(b => b.id === bundleId || b.bundle_code === bundleId);
        if (bundle && !bundleId.toString().startsWith('cellular_')) {
          // Regular bundle - save to user_bundles
          const { error: bundleError } = await supabase
            .from('user_bundles')
            .insert({
              user_id: user.id,
              bundle_code: bundle.bundle_code,
              monthly_cost: bundle.monthly_cost || 0,
              payment_method: 'direct',
              is_active: true
            });
          
          if (bundleError) {
            console.error('Error saving bundle:', bundleError);
          }
        }
        // Cellular perks will be handled separately in stage 2 onboarding
      }
    }

    // Save individual subscriptions (excluding those in bundles)
    const bundledServices = getServicesInBundles();
    const individualServices = currentServices.filter(s => !bundledServices.has(s));
    
    if (individualServices.length > 0) {
      const subscriptionsToInsert = individualServices.map(service => ({
        user_id: user.id,
        service_name: service,
        monthly_cost: 0,
        billing_cycle: 'monthly'
      }));

      const { error: subsError } = await supabase
        .from('subscriptions')
        .insert(subscriptionsToInsert);

      if (subsError) throw subsError;
    }

    setShowOnboarding(false);
    setProfileCompleted(false);
    setShowProfilePrompt(true);
  } catch (error) {
    console.error('Error saving onboarding data:', error);
    alert('Failed to save your information. Please try again.');
  }
};



  // Go to next onboarding step
  const nextStep = () => {
    if (onboardingStep === 1 && zipCode.length === 5) {
      setOnboardingStep(2);
    } else if (onboardingStep === 2 && internetProvider) {
      setOnboardingStep(3);
    } else if (onboardingStep === 3) {
      // Cellular provider step - can skip (cellularProvider can be empty)
      setOnboardingStep(4);
    } else if (onboardingStep === 4) {
      // Bundles step
      setOnboardingStep(5);
    } else if (onboardingStep === 5) {
      // Streaming services step
      setOnboardingStep(6);
    }
  };

  // Go to previous step
  const prevStep = () => {
    if (onboardingStep > 1) {
      setOnboardingStep(onboardingStep - 1);
    }
  };

 useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load reference data from database
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        // Load streaming services
        const { data: services } = await supabase
          .from('streaming_services')
          .select('service_name')
          .eq('is_active', true)
          .order('service_name');
        
        if (services && services.length > 0) {
          setRefStreamingServices(services.map(s => s.service_name));
        }

        // Load ISPs from isp_perks (get unique ISP names) - used as fallback
        const { data: isps } = await supabase
          .from('isp_perks')
          .select('*')
          .eq('is_active', true);
        
        if (isps && isps.length > 0) {
          const uniqueIsps = [...new Set(isps.map(p => p.isp_name))].sort();
          setRefInternetProviders([...uniqueIsps, 'Other', 'None / Not sure']);
          setIspPerks(isps);
        }

        // Load ISP plans for plan selection
        const { data: plans } = await supabase
          .from('isp_plans')
          .select('*')
          .order('sort_order');
        
        if (plans && plans.length > 0) {
          setIspPlans(plans);
        }

        // Load cellular providers
        const { data: cellProviders } = await supabase
          .from('cellular_providers')
          .select('*')
          .eq('is_active', true)
          .order('provider_name');
        
        if (cellProviders && cellProviders.length > 0) {
          setCellularProviders(cellProviders);
        }

        // Load cellular plans
        const { data: cellPlans } = await supabase
          .from('cellular_plans')
          .select('*, cellular_providers(provider_name, provider_code)')
          .eq('is_active', true)
          .order('sort_order');
        
        if (cellPlans && cellPlans.length > 0) {
          setCellularPlans(cellPlans);
        }

        // Load cellular perks
        const { data: cellPerks } = await supabase
          .from('cellular_perks')
          .select('*')
          .eq('is_active', true);
        
        if (cellPerks && cellPerks.length > 0) {
          setCellularPerks(cellPerks);
        }
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };

    loadReferenceData();
  }, []);

  // Check if user has completed onboarding and load their data
useEffect(() => {
  const loadUserData = async () => {
    if (!user) return;

    setShowProfile(false);
    
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('zip_code, internet_provider, internet_plan, is_student, is_senior, is_veteran, is_first_responder, onboarding_completed, plan_type')
        .eq('id', user.id)
        .single();

      if (error) {
        // User record doesn't exist yet - show onboarding
        console.log('No user record found, showing onboarding');
        setOnboardingStep(1);
        setZipCode('');
        setInternetProvider('');
        setSelectedPlan('');
        setCurrentServices([]);
        setIsStudent(false);
        setIsSenior(false);
        setIsVeteran(false);
        setIsFirstResponder(false);
        setShowOnboarding(true);
        setProfileCompleted(false);
        setUserPlanType('free');
        return;
      }

      // Set user's plan type
      setUserPlanType(userData.plan_type || 'free');

      // Check if user has completed basic onboarding (stage 1)
      if (userData?.zip_code && userData?.internet_provider) {
        setZipCode(userData.zip_code);
        setInternetProvider(userData.internet_provider);
        setSelectedPlan(userData.internet_plan || '');
        setIsStudent(userData.is_student || false);
        setIsSenior(userData.is_senior || false);
        setIsVeteran(userData.is_veteran || false);
        setIsFirstResponder(userData.is_first_responder || false);
        
        const { data: subs } = await supabase
          .from('subscriptions')
          .select('service_name')
          .eq('user_id', user.id);
        
        if (subs) {
          setCurrentServices(subs.map(s => s.service_name));
        }
        
        setShowOnboarding(false);
        
        // Check if profile completion (stage 2) is done
        if (!userData.onboarding_completed) {
          setProfileCompleted(false);
          setShowProfilePrompt(true);
        } else {
          setProfileCompleted(true);
          setShowProfilePrompt(false);
        }
      } else {
        // No data yet, show stage 1 onboarding
        setOnboardingStep(1);
        setZipCode('');
        setInternetProvider('');
        setCurrentServices([]);
        setIsStudent(false);
        setIsSenior(false);
        setIsVeteran(false);
        setIsFirstResponder(false);
        setShowOnboarding(true);
        setProfileCompleted(false);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setShowOnboarding(true);
    }
  };

loadUserData();
}, [user?.id]);

// Fetch financial optimization data for AI context
// FIXED: Now properly includes bundle allocations in credit card usage calculation
const fetchFinancialData = async () => {
  
  if (!user) return null;
  
  try {
    // Get subscriptions with full details
    const { data: subs } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', user.id);
    
    // Get credit card allocations - include BOTH subscription and bundle joins
    const { data: allocations } = await supabase
      .from('credit_card_allocations')
      .select('*, subscriptions(service_name, subscription_tier), user_bundles(bundle_code, actual_cost)')
      .eq('user_id', user.id);
    
    // Get user bundles for bundle name lookup (includes credit allocations to bundles)
    const { data: userBundles } = await supabase
      .from('user_bundles')
      .select('*, bundles(bundle_name)')
      .eq('user_id', user.id)
      .eq('is_active', true);
    
    // Get active perks with relationships
    const { data: perks } = await supabase
      .from('active_perks')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true);
    
    // Get full credit card data
    const { data: fullCreditCards } = await supabase
      .from('user_credit_cards')
      .select('*')
      .eq('user_id', user.id);
    
    // Get memberships
    const { data: userMemberships } = await supabase
      .from('user_memberships')
      .select('*')
      .eq('user_id', user.id);
    
    // Calculate totals and categorize services
    let totalPaidOutOfPocket = 0;
    let totalCoveredByCredits = 0;
    const paidServices = [];
    const creditCoveredServices = [];
    const freeServices = [];
    const bundleServices = [];
    
    subs?.forEach(sub => {
      const cost = parseFloat(sub.monthly_cost) || 0;
      const credit = parseFloat(sub.credit_applied) || 0;
      const actual = parseFloat(sub.actual_cost) || 0;
      
      const serviceDetail = {
        name: sub.service_name,
        tier: sub.subscription_tier,
        monthly_cost: cost,
        credit_applied: credit,
        actual_cost: actual,
        billing_cycle: sub.billing_cycle
      };
      
      if (sub.payment_method === 'paid') {
        totalPaidOutOfPocket += actual > 0 ? actual : cost;
        paidServices.push({
          ...serviceDetail,
          status: 'PAYING OUT OF POCKET'
        });
      } else if (sub.payment_method === 'credit_card_perk' || sub.payment_method === 'card_perk') {
        const card = fullCreditCards?.find(c => 
          c.card_name === sub.payment_source || 
          c.id === sub.payment_source
        );
        totalCoveredByCredits += credit;
        const youPay = Math.max(0, cost - credit);
        totalPaidOutOfPocket += youPay;
        
        if (youPay === 0) {
          creditCoveredServices.push({
            ...serviceDetail,
            card_name: card?.card_name || sub.payment_source,
            you_pay: 0,
            status: 'FULLY COVERED BY CREDIT CARD STREAMING CREDITS'
          });
        } else {
          creditCoveredServices.push({
            ...serviceDetail,
            card_name: card?.card_name || sub.payment_source,
            you_pay: youPay,
            status: 'PARTIALLY COVERED BY CREDIT CARD STREAMING CREDITS'
          });
        }
      } else if (sub.payment_method === 'membership_perk' || sub.payment_method === 'membership_benefit') {
        const membership = userMemberships?.find(m => 
          m.membership_type === sub.payment_source ||
          m.membership_name === sub.payment_source ||
          m.id === sub.payment_source
        );
        freeServices.push({
          ...serviceDetail,
          membership_name: membership?.membership_name || sub.payment_source,
          status: 'FREE VIA MEMBERSHIP'
        });
      } else if (sub.payment_method === 'bundle') {
        bundleServices.push({
          ...serviceDetail,
          bundle_code: sub.payment_source,
          bundle_cost: actual,
          status: 'PART OF BUNDLE'
        });
      } else if (sub.payment_method === 'isp_bundle' || sub.payment_method === 'isp_perk') {
        freeServices.push({
          ...serviceDetail,
          isp_name: sub.payment_source,
          status: 'FREE VIA INTERNET PROVIDER'
        });
      } else if (sub.payment_method === 'nested_perk') {
        freeServices.push({
          ...serviceDetail,
          source: sub.payment_source,
          status: 'FREE VIA CREDIT CARD MEMBERSHIP PERK'
        });
      } else {
        paidServices.push({
          ...serviceDetail,
          status: 'PAYMENT METHOD NOT SET - NEEDS CONFIGURATION'
        });
        totalPaidOutOfPocket += cost || 0;
      }
    });
    
    // Calculate credit card usage - includes BOTH service and bundle allocations
    // After the fix, bundle allocations are stored in credit_card_allocations with target_type='bundle'
    // But for backwards compatibility, also check user_bundles.credit_applied for older data
    const creditCardUsage = fullCreditCards?.map(card => {
      // Get ALL allocations for this card from credit_card_allocations table
      const cardAllocations = allocations?.filter(a => a.credit_card_id === card.id) || [];
      
      // Split into service allocations and bundle allocations
      const serviceAllocations = cardAllocations.filter(a => a.target_type === 'subscription' || (a.subscription_id && !a.bundle_id));
      const bundleAllocationsFromTable = cardAllocations.filter(a => a.target_type === 'bundle' || a.bundle_id);
      
      // Calculate totals from credit_card_allocations
      const serviceAllocationsTotal = serviceAllocations.reduce((sum, a) => sum + parseFloat(a.amount_allocated || 0), 0);
      const bundleAllocationsFromTableTotal = bundleAllocationsFromTable.reduce((sum, a) => sum + parseFloat(a.amount_allocated || 0), 0);
      
      // BACKWARDS COMPATIBILITY: Also check user_bundles for allocations not yet in credit_card_allocations
      // This handles data created before the fix was deployed
      const bundleIdsInTable = bundleAllocationsFromTable.map(a => a.bundle_id);
      const bundleAllocationsFromUserBundles = (userBundles || []).filter(b => 
        b.payment_method === 'credit_card_perk' && 
        (b.payment_source === card.card_name || b.payment_source === card.id) &&
        parseFloat(b.credit_applied || 0) > 0 &&
        !bundleIdsInTable.includes(b.id) // Don't double count
      );
      const bundleAllocationsFromUserBundlesTotal = bundleAllocationsFromUserBundles.reduce((sum, b) => sum + parseFloat(b.credit_applied || 0), 0);
      
      // Total used is sum of all sources
      const totalUsed = serviceAllocationsTotal + bundleAllocationsFromTableTotal + bundleAllocationsFromUserBundlesTotal;
      const monthlyCredit = parseFloat(card.streaming_credit_monthly) || 0;
      const remaining = monthlyCredit - totalUsed;
      
      // Build combined allocations list for AI
      const allocationsList = [
        // Service allocations
        ...serviceAllocations.map(a => ({
          service: a.subscriptions?.service_name || 'Unknown Service',
          type: 'service',
          tier: a.subscriptions?.subscription_tier,
          amount: parseFloat(a.amount_allocated)
        })),
        // Bundle allocations from credit_card_allocations table
        ...bundleAllocationsFromTable.map(a => {
          const bundle = userBundles?.find(b => b.id === a.bundle_id);
          const bundleName = bundle?.bundles?.bundle_name || bundle?.bundle_code || 'Unknown Bundle';
          return {
            service: bundleName,
            type: 'bundle',
            tier: null,
            amount: parseFloat(a.amount_allocated)
          };
        }),
        // Bundle allocations from user_bundles (backwards compatibility)
        ...bundleAllocationsFromUserBundles.map(b => {
          const bundleName = b.bundles?.bundle_name || b.bundle_code || 'Unknown Bundle';
          return {
            service: bundleName,
            type: 'bundle',
            tier: null,
            amount: parseFloat(b.credit_applied)
          };
        })
      ];
      
      return {
        card_name: card.card_name,
        issuer: card.issuer,
        monthly_credit: monthlyCredit,
        used: totalUsed,
        remaining: remaining,
        allocations: allocationsList,
        status: monthlyCredit > 0 ? (remaining > 0.01 ? 'HAS UNUSED CREDITS' : 'FULLY ALLOCATED') : 'NO STREAMING CREDITS'
      };
    }) || [];
    
    // Build perk chains
    const perkChains = [];
    perks?.forEach(perk => {
      const chain = {
        source: perk.perk_source,
        service: perk.service_name,
        tier: perk.tier
      };
      
      const sourceCard = fullCreditCards?.find(card => 
        perk.perk_source?.toLowerCase().includes(card.card_name?.toLowerCase())
      );
      
      if (sourceCard) {
        chain.chain = sourceCard.card_name + ' → ' + perk.service_name;
      } else {
        const sourceMembership = userMemberships?.find(m => 
          perk.perk_source?.toLowerCase().includes(m.membership_name?.toLowerCase())
        );
        
        if (sourceMembership) {
          const cardForMembership = fullCreditCards?.find(card =>
            card.card_name?.toLowerCase().includes('amex platinum') && 
            sourceMembership.membership_name?.toLowerCase().includes('walmart')
          );
          
          if (cardForMembership) {
            chain.chain = cardForMembership.card_name + ' → ' + sourceMembership.membership_name + ' → ' + perk.service_name;
          } else {
            chain.chain = sourceMembership.membership_name + ' → ' + perk.service_name;
          }
        }
      }
      
      perkChains.push(chain);
    });
    
    return {
      paidServices,
      creditCoveredServices,
      freeServices,
      bundleServices,
      creditCardUsage,
      perkChains,
      totals: {
        paidOutOfPocket: totalPaidOutOfPocket,
        coveredByCredits: totalCoveredByCredits,
        totalValue: totalPaidOutOfPocket + totalCoveredByCredits
      }
    };
  } catch (error) {
    console.error('Error fetching financial data:', error);
    return null;
  }
};

  const handleQuery = async (presetQuery = null) => {
  const queryText = presetQuery || query;
  if (!queryText.trim()) return;

  // Only add user message if not a preset (preset already added it)
  if (!presetQuery) {
    const userMessage = { role: 'user', content: queryText };
    setMessages(prev => [...prev, userMessage]);
  }
  const currentQuery = queryText;
  setQuery('');
  setLoading(true);

  try {
    // Check query limit for free users
    const { data: userData } = await supabase
      .from('users')
      .select('plan_type, queries_used_month')
      .eq('id', user.id)
      .single();

    // If free tier and at/over limit, block the query
    if (userData?.plan_type === 'free' && userData?.queries_used_month >= 5) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "You've used all 5 free queries this month! 🐘\n\nUpgrade to Stomp Premium for:\n✅ Unlimited AI queries\n✅ Real-time perk alerts\n✅ Deal expiration reminders\n\nJust $4.99/month - less than one unused subscription!\n\n[Upgrade feature coming soon - contact us to upgrade early!]"
      }]);
      setLoading(false);
      return;
    }

// Fetch user's credit cards
const { data: creditCards } = await supabase
  .from('user_credit_cards')
  .select('card_name, issuer')
  .eq('user_id', user.id);

// Fetch user's memberships
const { data: memberships } = await supabase
  .from('user_memberships')
  .select('membership_name, membership_type')
  .eq('user_id', user.id);

// Fetch user's eligibility flags
const { data: userProfile } = await supabase
  .from('users')
  .select('is_student, is_senior, is_veteran, is_first_responder')
  .eq('id', user.id)
  .single();

// Fetch financial optimization data
const financialData = await fetchFinancialData();

// Call our backend API (which talks to Claude securely)
const response = await fetch('/api/chat', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    query: currentQuery,
    conversationHistory: messages, // Send existing conversation history
    userId: user.id, // For query logging
    userInfo: {
      zipCode: zipCode,
      internetProvider: internetProvider,
      currentServices: currentServices,
      creditCards: creditCards || [],
      memberships: memberships || [],
      eligibility: {
        isStudent: userProfile?.is_student || false,
        isSenior: userProfile?.is_senior || false,
        isVeteran: userProfile?.is_veteran || false,
        isFirstResponder: userProfile?.is_first_responder || false
      },
      financialData: financialData
    }
  })
}); 

if (!response.ok) {
  const errorData = await response.json().catch(() => ({}));
  const errorMessage = errorData.message || 'Something went wrong. Please try again.';
  
  setMessages(prev => [...prev, {
    role: 'assistant',
    content: errorMessage
  }]);
  setLoading(false);
  return;
}

    const data = await response.json();
    
  setMessages(prev => [...prev, {
  role: 'assistant',
  content: data.response,
  chatLogId: data.chatLogId  // For feedback tracking
}]);

    // Increment query count for free users
    if (userData?.plan_type === 'free') {
      await supabase
        .from('users')
        .update({ 
          queries_used_month: (userData.queries_used_month || 0) + 1 
        })
        .eq('id', user.id);
    }

  } catch (error) {
    console.error('Error:', error);
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Sorry, I encountered an error. Please try again.'
    }]);
  } finally {
    setLoading(false);
  }
};


// Show loading while checking auth OR while loading user data for logged-in user
if (authLoading || (user && showOnboarding === null)) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
      <Loader className="w-12 h-12 text-white animate-spin" />
    </div>
  );
}

  // Route checks for Terms and Privacy (accessible without login)
  if (window.location.pathname === '/terms') {
    return <Terms />;
  }
  
  if (window.location.pathname === '/privacy') {
    return <Privacy />;
  }

  if (window.location.pathname === '/auth/confirm') {
  return <AuthConfirm />;
}
if (window.location.pathname === '/auth/reset-password') {
  return <ResetPassword />;
}

// Show auth screens if not logged in
if (!user) {
  const path = window.location.pathname;
  
  if (path === '/signup') {
    return (
      <SignUp 
        onSwitchToLogin={() => window.location.href = '/login'}
        onSignUpSuccess={() => {}}
      />
    );
  } else if (path === '/login') {
    return (
      <Login 
        onSwitchToSignUp={() => window.location.href = '/signup'}
        onLoginSuccess={() => {}}
      />
    );
  } else {
    return (
      <Landing
        onShowLogin={() => window.location.href = '/login'}
        onShowSignUp={() => window.location.href = '/signup'}
      />
    );
  }
}

// Show profile page
if (showProfile) {
  return <SmartProfileEditor onBack={() => setShowProfile(false)} />;
}

// Show profile completion wizard (stage 2 onboarding)
if (showProfileWizard) {
  return (
    <OnboardingWizard
      onComplete={() => {
        setShowProfileWizard(false);
        setProfileCompleted(true);
        setShowProfilePrompt(false);
      }}
      onSkip={() => {
        setShowProfileWizard(false);
        setShowProfilePrompt(true);
      }}
    />
  );
}

  return (
  <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 relative">
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto">
        
        {/* ONBOARDING WIZARD */}
        {showOnboarding ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
              
              {/* Header */}
              <div className="text-center mb-8">
                <div className="mb-4">
                  <Logo size="default" variant="gradient" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Welcome! 🎉
                </h1>
                <p className="text-gray-600">
                   Never forget a perk. Never overpay again.
                </p>
                
                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-2 mt-6">
                   <div className={`w-3 h-3 rounded-full ${onboardingStep >= 1 ? 'bg-purple-600' : 'bg-gray-300'}`} />
                   <div className={`w-3 h-3 rounded-full ${onboardingStep >= 2 ? 'bg-purple-600' : 'bg-gray-300'}`} />
                   <div className={`w-3 h-3 rounded-full ${onboardingStep >= 3 ? 'bg-purple-600' : 'bg-gray-300'}`} />
                   <div className={`w-3 h-3 rounded-full ${onboardingStep >= 4 ? 'bg-purple-600' : 'bg-gray-300'}`} />
                   <div className={`w-3 h-3 rounded-full ${onboardingStep >= 5 ? 'bg-purple-600' : 'bg-gray-300'}`} />
                   <div className={`w-3 h-3 rounded-full ${onboardingStep >= 6 ? 'bg-purple-600' : 'bg-gray-300'}`} />
                </div>
              </div>

              {/* STEP 1: Zip Code */}
              {onboardingStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-2">
                      What's your zip code?
                    </label>
                    <p className="text-sm text-gray-600 mb-4">
                      This helps us show you local provider options and availability
                    </p>
                    <input
                      type="text"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="Enter 5-digit zip code"
                      className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      maxLength="5"
                    />
                  </div>
                  
                  <button
                    onClick={nextStep}
                    disabled={zipCode.length !== 5}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg transition-all text-lg"
                  >
                    Continue
                  </button>
                </div>
              )}

              {/* STEP 2: Internet Provider */}
              {onboardingStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-2">
                      Who's your internet provider?
                    </label>
                    <p className="text-sm text-gray-600 mb-4">
                      Many internet providers include free streaming services!
                    </p>
                    
                    {loadingISPs ? (
                      <div className="flex items-center justify-center py-8 text-gray-500">
                        <Loader className="w-6 h-6 animate-spin mr-2" />
                        Finding providers in your area...
                      </div>
                    ) : (
                      <>
                        {ispCoverage.length > 0 && (
                          <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg mb-4">
                            📍 Based on your location ({zipCode}), here are the providers in your area:
                          </p>
                        )}
                        
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {internetProviders.map((isp) => {
                            const ispName = isp.isp_name || isp;
                            const perks = getISPPerks(ispName);
                            const hasPerks = perks.length > 0;
                            
                            return (
                              <label
                                key={ispName}
                                className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  internetProvider === ispName
                                    ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                    : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="isp"
                                  checked={internetProvider === ispName}
                                  onChange={() => { setInternetProvider(ispName); setSelectedPlan(''); }}
                                  className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-gray-900">{ispName}</span>
                                    {hasPerks && (
                                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                        🎁 Streaming perks
                                      </span>
                                    )}
                                  </div>
                                  {isp.coverage_type && (
                                    <p className="text-sm text-gray-500 mt-0.5 capitalize">
                                      {isp.coverage_type === 'fixed_wireless' ? '5G/Fixed Wireless' : isp.coverage_type}
                                    </p>
                                  )}
                                  {hasPerks && internetProvider === ispName && (
                                    <p className="mt-1 text-sm text-green-700">
                                      Includes: {perks.map(p => p.service_name).join(', ')}
                                    </p>
                                  )}
                                  
                                  {/* Plan selection */}
                                  {internetProvider === ispName && getPlansForISP(ispName).length > 1 && (
                                    <div className="mt-3 pt-3 border-t border-purple-200">
                                      <p className="font-medium text-purple-900 mb-2 text-sm">Which plan do you have?</p>
                                      <div className="space-y-1.5">
                                        {getPlansForISP(ispName).map((plan, planIdx) => (
                                          <label
                                            key={plan.id || plan.plan_name + '-' + planIdx}
                                            className={`flex items-center gap-2 p-2 bg-white border rounded cursor-pointer transition-all text-sm ${
                                              selectedPlan === plan.plan_name
                                                ? 'border-purple-500 ring-1 ring-purple-200'
                                                : 'border-gray-200 hover:border-purple-300'
                                            }`}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <input
                                              type="radio"
                                              name="isp_plan"
                                              checked={selectedPlan === plan.plan_name}
                                              onChange={() => setSelectedPlan(plan.plan_name)}
                                              className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                            />
                                            <span className="font-medium text-gray-900">{plan.plan_name}</span>
                                            {plan.speed_down_mbps && (
                                              <span className="text-gray-400 text-xs ml-auto">
                                                {plan.speed_down_mbps >= 1000 ? (plan.speed_down_mbps/1000) + ' Gig' : plan.speed_down_mbps + ' Mbps'}
                                              </span>
                                            )}
                                          </label>
                                        ))}
                                        <label
                                          className={`flex items-center gap-2 p-2 bg-white border rounded cursor-pointer transition-all text-sm ${
                                            selectedPlan === 'not_sure'
                                              ? 'border-purple-500 ring-1 ring-purple-200'
                                              : 'border-gray-200 hover:border-purple-300'
                                          }`}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <input
                                            type="radio"
                                            name="isp_plan"
                                            checked={selectedPlan === 'not_sure'}
                                            onChange={() => setSelectedPlan('not_sure')}
                                            className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                          />
                                          <span className="text-gray-700">I'm not sure</span>
                                        </label>
                                        <p className="text-xs text-gray-400 mt-2">Not all plans may be available at your address.</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                          
                          {/* No internet option */}
                          <label
                            className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              internetProvider === 'None / Not sure'
                                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="isp"
                              checked={internetProvider === 'None / Not sure'}
                              onChange={() => { setInternetProvider('None / Not sure'); setSelectedPlan(''); }}
                              className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900">None / Not sure</span>
                              <p className="text-sm text-gray-500">I don't have home internet or I'm not sure</p>
                            </div>
                          </label>
                          
                          {/* Other option */}
                          <label
                            className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              internetProvider === 'Other'
                                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="isp"
                              checked={internetProvider === 'Other'}
                              onChange={() => { setInternetProvider('Other'); setSelectedPlan(''); }}
                              className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900">Other</span>
                              <p className="text-sm text-gray-500">My provider isn't listed</p>
                            </div>
                          </label>
                        </div>
                        
                        <p className="text-xs text-gray-400 mt-3 text-center">
                          Provider availability based on your zip code. Actual service options may vary.
                        </p>
                      </>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={prevStep}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-lg transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={nextStep}
                      disabled={!internetProvider || (
                        internetProvider !== 'None / Not sure' && 
                        internetProvider !== 'Other' && 
                        getPlansForISP(internetProvider).length > 1 && 
                        !selectedPlan
                      )}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg transition-all"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Cellular Provider */}
              {onboardingStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-2">
                      Who's your cell phone provider?
                    </label>
                    <p className="text-sm text-gray-600 mb-4">
                      Many carriers include free streaming services with their plans!
                    </p>
                    
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {cellularProviders.filter(p => p.provider_code !== 'other').map((provider) => {
                        const plans = getPlansForCellular(provider.provider_code);
                        const perks = getPerksForCellularPlan(provider.provider_code, cellularPlan);
                        const hasPerks = provider.has_streaming_perks;
                        
                        return (
                          <label
                            key={provider.provider_code}
                            className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              cellularProvider === provider.provider_code
                                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="cellular"
                              checked={cellularProvider === provider.provider_code}
                              onChange={() => { setCellularProvider(provider.provider_code); setCellularPlan(''); }}
                              className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-900">{provider.display_name}</span>
                                {hasPerks && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                    🎁 Streaming perks
                                  </span>
                                )}
                              </div>
                              
                              {/* Plan selection */}
                              {cellularProvider === provider.provider_code && plans.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-purple-200">
                                  <p className="font-medium text-purple-900 mb-2 text-sm">Which plan do you have?</p>
                                  <div className="space-y-1.5">
                                    {plans.filter(p => p.plan_code !== 'not_sure').map((plan) => (
                                      <label
                                        key={plan.id}
                                        className={`flex items-center gap-2 p-2 bg-white border rounded cursor-pointer transition-all text-sm ${
                                          cellularPlan === plan.plan_code
                                            ? 'border-purple-500 ring-1 ring-purple-200'
                                            : 'border-gray-200 hover:border-purple-300'
                                        }`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="radio"
                                          name="cellular_plan"
                                          checked={cellularPlan === plan.plan_code}
                                          onChange={() => setCellularPlan(plan.plan_code)}
                                          className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="font-medium text-gray-900">{plan.plan_name}</span>
                                      </label>
                                    ))}
                                    <label
                                      className={`flex items-center gap-2 p-2 bg-white border rounded cursor-pointer transition-all text-sm ${
                                        cellularPlan === 'not_sure'
                                          ? 'border-purple-500 ring-1 ring-purple-200'
                                          : 'border-gray-200 hover:border-purple-300'
                                      }`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="radio"
                                        name="cellular_plan"
                                        checked={cellularPlan === 'not_sure'}
                                        onChange={() => setCellularPlan('not_sure')}
                                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                      />
                                      <span className="text-gray-700">I'm not sure</span>
                                    </label>
                                  </div>
                                  
                                  {/* Number of lines */}
                                  <div className="mt-3 pt-3 border-t border-purple-200">
                                    <p className="font-medium text-purple-900 mb-2 text-sm">How many lines on your account?</p>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setCellularLines(Math.max(1, cellularLines - 1)); }}
                                        className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                                      >
                                        -
                                      </button>
                                      <span className="text-lg font-medium w-8 text-center">{cellularLines}</span>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setCellularLines(cellularLines + 1); }}
                                        className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                                      >
                                        +
                                      </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">Some perks require 2+ lines</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                      
                      {/* None / Skip option */}
                      <label
                        className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          cellularProvider === 'none'
                            ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="cellular"
                          checked={cellularProvider === 'none'}
                          onChange={() => { setCellularProvider('none'); setCellularPlan(''); }}
                          className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500"
                        />
                        <div>
                          <span className="font-medium text-gray-900">Skip / Other</span>
                          <p className="text-sm text-gray-500">I'll add this later or my carrier isn't listed</p>
                        </div>
                      </label>
                    </div>
                    
                    {/* Convergence notification */}
                    {checkConvergenceEligibility() && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          🎉 <strong>Bundle bonus!</strong> You may qualify for extra savings by having both cell and home internet with the same provider!
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={prevStep}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-lg transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={nextStep}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-lg transition-all"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: Existing Bundles */}
              {onboardingStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-2">
                      Do you already have any streaming bundles?
                    </label>
                    <p className="text-sm text-gray-600 mb-4">
                      Select any bundles you're currently subscribed to
                    </p>
                    
                    {loadingBundles ? (
                      <div className="flex items-center justify-center py-8 text-gray-500">
                        <Loader className="w-6 h-6 animate-spin mr-2" />
                        Finding available bundles...
                      </div>
                    ) : availableBundles.length > 0 ? (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {availableBundles.map((bundle) => (
                          <label
                            key={bundle.id || bundle.bundle_code}
                            className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              selectedBundles.includes(bundle.id || bundle.bundle_code)
                                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedBundles.includes(bundle.id || bundle.bundle_code)}
                              onChange={() => toggleBundle(bundle.id || bundle.bundle_code)}
                              className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-900">{bundle.bundle_name}</span>
                                {bundle.monthly_cost === 0 && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                                    FREE
                                  </span>
                                )}
                                {bundle.source_type === 'cellular_perk' && (
                                  <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                    📱 Cell perk
                                  </span>
                                )}
                              </div>
                              {bundle.description && (
                                <p className="text-sm text-gray-500 mt-1">{bundle.description}</p>
                              )}
                              {bundle.retail_value && bundle.monthly_cost === 0 && (
                                <p className="text-sm text-green-600 mt-1">
                                  ${bundle.retail_value}/mo value included free!
                                </p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p>No bundles found based on your selections.</p>
                        <p className="text-sm mt-1">You can add bundles later in your profile.</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={prevStep}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-lg transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={nextStep}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-lg transition-all"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 5: Current Streaming Services */}
              {onboardingStep === 5 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-2">
                      What other streaming services do you pay for individually?
                    </label>
                    <p className="text-sm text-gray-600 mb-4">
                      Select services you pay for separately (not included in bundles above)
                    </p>
                    
                    {selectedBundles.length > 0 && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          ℹ️ Services from your selected bundles are already tracked. Only select services you pay for separately.
                        </p>
                      </div>
                    )}
                    
                    <div className="max-h-96 overflow-y-auto border-2 border-gray-200 rounded-lg p-4 space-y-2">
                      {streamingServices.map(service => {
                        const isInBundle = getServicesInBundles().has(service);
                        return (
                          <label
                            key={service}
                            className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                              isInBundle 
                                ? 'bg-gray-100 opacity-50 cursor-not-allowed' 
                                : 'hover:bg-purple-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={currentServices.includes(service)}
                              onChange={() => !isInBundle && toggleService(service)}
                              disabled={isInBundle}
                              className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                            />
                            <span className={`ml-3 ${isInBundle ? 'text-gray-400' : 'text-gray-900'}`}>
                              {service}
                              {isInBundle && <span className="ml-2 text-xs text-gray-400">(in bundle)</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={prevStep}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-lg transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={nextStep}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-lg transition-all"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 6: Quick Eligibility */}
              {onboardingStep === 6 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-900 mb-2">
                      Do any of these apply to you?
                    </label>
                    <p className="text-sm text-gray-600 mb-4">
                      Select all that apply - helps us find you the best discounts!
                    </p>
                    
                    <div className="space-y-3">
                      <label className="flex items-center p-4 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors border-2 border-gray-200">
                        <input
                          type="checkbox"
                          checked={isStudent}
                          onChange={(e) => setIsStudent(e.target.checked)}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <div className="ml-3">
                          <span className="text-gray-900 font-medium">Student</span>
                          <p className="text-sm text-gray-500">Active student with .edu email</p>
                        </div>
                      </label>

                      <label className="flex items-center p-4 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors border-2 border-gray-200">
                        <input
                          type="checkbox"
                          checked={isSenior}
                          onChange={(e) => setIsSenior(e.target.checked)}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <div className="ml-3">
                          <span className="text-gray-900 font-medium">Senior (55+)</span>
                          <p className="text-sm text-gray-500">Age 55 or older</p>
                        </div>
                      </label>

                      <label className="flex items-center p-4 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors border-2 border-gray-200">
                        <input
                          type="checkbox"
                          checked={isVeteran}
                          onChange={(e) => setIsVeteran(e.target.checked)}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <div className="ml-3">
                          <span className="text-gray-900 font-medium">Veteran or Active Military</span>
                          <p className="text-sm text-gray-500">Current or former military service</p>
                        </div>
                      </label>

                      <label className="flex items-center p-4 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors border-2 border-gray-200">
                        <input
                          type="checkbox"
                          checked={isFirstResponder}
                          onChange={(e) => setIsFirstResponder(e.target.checked)}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <div className="ml-3">
                          <span className="text-gray-900 font-medium">First Responder</span>
                          <p className="text-sm text-gray-500">Police, Fire, EMT, or Healthcare worker</p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={prevStep}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 rounded-lg transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={completeOnboarding}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-lg transition-all"
                    >
                      Start Saving! 🚀
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* MAIN CHAT INTERFACE */}

            {/* Header Row 1: Welcome + Profile */}
            <div className="flex items-center justify-between mb-3">
              <WelcomeBanner variant="gradient" />
              <button
                onClick={() => setShowProfile(true)}
                className="flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg transition-colors text-sm"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="hidden sm:inline">Profile</span>
              </button>
            </div>

            {/* Header Row 2: Upgrade button centered */}
            {userPlanType !== 'premium' && (
              <div className="flex justify-center mb-4">
                <UpgradeBanner variant="header" />
              </div>
            )}
            {userPlanType === 'premium' && (
              <div className="flex justify-center mb-4">
                <PremiumBadge />
              </div>
            )}

            {/* Profile completion prompt - flows naturally below header */}
            {showProfilePrompt && !profileCompleted && (
              <OnboardingPrompt
                onStart={() => {
                  setShowProfilePrompt(false);
                  setShowProfileWizard(true);
                }}
                onDismiss={() => setShowProfilePrompt(false)}
              />
            )}

            {/* Centered Logo Section */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Logo size="large" variant="glow" />
              </div>
              <p className="text-purple-200 text-base sm:text-lg px-4">
                Never forget a perk. Never overpay again. 🐘
              </p>
            </div>

            {/* Chat Container */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Messages */}
              <div className="min-h-[300px] md:h-[500px] md:overflow-y-auto p-6 space-y-4">
                {/* Preset questions - always visible at top */}
                <div className="text-center py-4 border-b border-gray-100 mb-4">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    <h2 className="text-lg font-semibold text-gray-800">
                      Quick Questions
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                    {[
                      "What's the best package deal for the services I have?",
                      "Where can I watch Yellowstone?",
                      "How can I save money on streaming?",
                      "Does my internet provider include any free services?"
                    ].map((example, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          // Add user message immediately
                          setMessages(prev => [...prev, { role: 'user', content: example }]);
                          // Call handleQuery with the preset question
                          handleQuery(example);
                        }}
                        disabled={loading}
                        className="p-3 text-left bg-purple-50 hover:bg-purple-100 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-lg text-sm text-gray-700 transition-colors"
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Welcome message when no messages yet */}
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      Click a question above or type your own below!
                    </p>
                  </div>
                )}

{messages.map((msg, idx) => (
  <div key={idx}>
    {msg.role === 'user' ? (
      <div className="flex justify-end">
        <div className="max-w-[80%] p-4 rounded-2xl bg-purple-600 text-white">
          {msg.content}
        </div>
      </div>
    ) : (
      <div className="flex justify-start">
        <div className="max-w-[90%] p-4 rounded-2xl bg-gray-100 text-gray-800">
          <div className="whitespace-pre-wrap">{msg.content}</div>
          {msg.chatLogId && (
            <FeedbackButtons chatLogId={msg.chatLogId} />
          )}
        </div>
      </div>
    )}
  </div>
))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 p-4 rounded-2xl">
                      <Loader className="w-6 h-6 animate-spin text-purple-600" />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
<div className="border-t border-gray-200 p-4 bg-gray-50">
  <form 
    onSubmit={(e) => {
      e.preventDefault();
      handleQuery();
    }}
    className="flex gap-2"
  >
    <input
      type="text"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      placeholder="Ask about streaming perks, bundles, or where to watch..."
      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
      disabled={loading}
    />
    <button
      type="submit"
      disabled={loading || !query.trim()}
      className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
    >
      <Send className="w-5 h-5" />
      <span className="hidden sm:inline">Ask</span>
    </button>
  </form>
</div>
            </div>
            {/* Logout Button */}
<div className="mt-4 text-center">
  <button
    onClick={signOut}
    className="text-sm text-purple-200 hover:text-white transition-colors"
  >
    Log Out
  </button>
</div>

{/* Affiliate Disclosure */}
<div className="mt-2 text-center text-xs text-purple-200">
  💡 Stomp earns affiliate commissions when you sign up through our links
</div>
          </>
        )}
      </div>
    </div>
  );
}